import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@cokitchen/cokitchen-components';
import { AddIcon } from 'cokitchen-icons';
import { StreakType } from 'utils/types';
import PageHeaderText from 'components/base/page-header-text';
import PageText from 'components/base/page-text';
import emptyDataImage from 'assets/images/empty-data-image.png';
import StreakCard from './streak-card';
import StreaksActionBox from './streaks-action-box';
import StreakFormModal from './streak-form-modal';
import { apiService } from 'services/base.service';
import useDashboardContext from 'contexts/dashboard.context';
import AccessDenied from 'components/access-denied';

const Streaks = () => {
  const mounting = useRef(true);
  const [streaks, setStreaks] = useState<StreakType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<StreakType[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [streakToEdit, setStreakToEdit] = useState<StreakType | null>(null);

  const { permissions } = useDashboardContext();
  const toggleSelection = (checked: boolean, streak: StreakType) => {
    const isSelected = selected.find((s) => s.id === streak.id);
    if (checked && !isSelected) setSelected((s) => [...s, streak]);
    else setSelected(selected.filter((s) => s.id !== streak.id));
  };

  const loadStreaks = async (showLoading?: boolean) => {
    if (showLoading && !loading) setLoading(true);
    try {
      const res = await apiService.get('loyalty-streak');
      setStreaks(res.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const editStreak = (streak: StreakType) => {
    setStreakToEdit(streak);
    setShowEditModal(true);
  };

  useEffect(() => {
    if (mounting.current) loadStreaks(true);
    return () => {
      mounting.current = false;
    };
  }, []);

  const canAccessRewards = permissions.canAccessRewards;
  if (!canAccessRewards) return <AccessDenied />;
  return (
    <div>
      <PageHeaderText title='Manage Streaks' description='Create, edit and maintain streaks here. '>
        <Button
          variant='link'
          color='primary'
          className='flex items-center gap-1'
          onClick={() => setShowCreateModal(true)}>
          <AddIcon />
          Add new
        </Button>
      </PageHeaderText>

      {loading ? (
        <PageText text='Loading...' />
      ) : !loading && streaks.length ? (
        <div className='grid gap-x-4 gap-y-6 grid-cols-1 sm:grid-cols-[repeat(auto-fill,_minmax(350px,_1fr))] pb-[100px]'>
          {streaks.map((streak, i) => (
            <StreakCard
              key={i}
              {...streak}
              hideEdit={true}
              selected={Boolean(selected.find((s) => s.id === streak.id))}
              onChange={(v) => toggleSelection(v, streak)}
              onEditClick={() => editStreak(streak)}
            />
          ))}
        </div>
      ) : (
        <PageText image={emptyDataImage} text="You haven't created any streak" />
      )}

      {selected.length ? (
        <StreaksActionBox
          items={selected}
          onSelectAll={() => setSelected(streaks)}
          onEdit={() => setShowBulkEditModal(true)}
          onDone={() => setSelected([])}
        />
      ) : null}

      {showCreateModal ? (
        <StreakFormModal
          action='create'
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          afterMutation={() => loadStreaks()}
        />
      ) : null}

      {showEditModal && streakToEdit ? (
        <StreakFormModal
          action='edit'
          streak={streakToEdit}
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          afterMutation={() => loadStreaks()}
        />
      ) : null}

      {showBulkEditModal && selected.length ? (
        <StreakFormModal
          action='bulk-edit'
          streaks={selected}
          open={showBulkEditModal}
          onClose={() => setShowBulkEditModal(false)}
          afterMutation={() => {
            loadStreaks();
            setSelected([]);
          }}
        />
      ) : null}
    </div>
  );
};

export default Streaks;
